import React from 'react';
import { motion } from 'framer-motion';
import { Download, Star, Clock, User, FileText, Loader } from 'lucide-react';
import type { Note } from '../hooks/useApi';
import { useDownloadNote } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useRatingPrompt } from '../contexts/RatingPromptContext';
import NoteDocumentPreview from './NoteDocumentPreview';
import { getNoteAverageRating } from '../utils/noteRatings';
import {
  subjectLabels,
  universityYearLabels,
  legacyGradeLabels,
  subjectColors,
  formatFileSize,
  timeAgo,
} from '../utils/noteLabels';
import { getNoteAuthorLabel } from '../utils/noteAuthorDisplay';

interface NoteCardProps {
  note: Note;
  index?: number;
  /** Ana sayfa gibi alanlarda misafirleri kayıt modalına yönlendir */
  registerPromptOnDownload?: boolean;
}

const REGISTER_DOWNLOAD_MESSAGE =
  'Henüz kayıt olmadın mı? Ücretsiz kayıt olarak notları indirebilirsin.';

const NoteCard: React.FC<NoteCardProps> = ({ note, index = 0, registerPromptOnDownload = false }) => {
  const { download, downloading } = useDownloadNote();
  const { isLoggedIn, requireAuth, openAuthModal } = useAuth();
  const { openRatingPrompt } = useRatingPrompt();
  const color = subjectColors[note.subject] || '#64748b';
  const isDownloading = downloading === note._id;
  const avgRating = getNoteAverageRating(note);

  const handleDownloadClick = () => {
    if (registerPromptOnDownload && !isLoggedIn) {
      openAuthModal('register', REGISTER_DOWNLOAD_MESSAGE);
      return;
    }

    requireAuth(async () => {
      try {
        await download(note._id, note.fileName || `${note.title}.pdf`, note.fileUrl);
        openRatingPrompt({ noteId: note._id, title: note.title });
      } catch {
        /* hata konsolda */
      }
    });
  };

  return (
    <motion.div
      className="glow-card"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      viewport={{ once: true }}
      style={{ padding: '28px', borderRadius: '20px', position: 'relative', overflow: 'hidden' }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '28px',
          right: '28px',
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          opacity: 0.5,
        }}
      />

      <NoteDocumentPreview fileUrl={note.fileUrl} fileName={note.fileName} height={150} />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '14px',
          gap: '12px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '10px',
                flexShrink: 0,
                background: `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileText size={16} color={color} />
            </div>
            <h3
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                fontWeight: 700,
                color: 'white',
                margin: 0,
                fontFamily: "'Space Grotesk', sans-serif",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {note.title}
            </h3>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '44px' }}>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  color={i < Math.round(avgRating) ? '#fbbf24' : '#1e293b'}
                  fill={i < Math.round(avgRating) ? '#fbbf24' : 'none'}
                />
              ))}
            </div>
            <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>
              {avgRating}
            </span>
            <span style={{ color: '#475569', fontSize: '11px' }}>
              ({note.downloads.toLocaleString('tr-TR')} indirme)
            </span>
          </div>
        </div>

        <div
          style={{
            background: 'var(--accent-primary-soft)',
            border: '1px solid var(--accent-primary-border)',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '11px',
            color: 'var(--accent-primary)',
            fontWeight: 600,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {formatFileSize(note.fileSize)}
        </div>
      </div>

      <p
        style={{
          color: '#94a3b8',
          marginBottom: '16px',
          lineHeight: 1.6,
          fontSize: '13px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {note.description}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '18px' }}>
        <span
          style={{
            background: `${color}10`,
            color,
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            border: `1px solid ${color}20`,
          }}
        >
          {subjectLabels[note.subject] || note.subject}
        </span>
        <span
          style={{
            background: 'rgba(245, 158, 11, 0.1)',
            color: '#f59e0b',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            border: '1px solid rgba(245, 158, 11, 0.2)',
          }}
        >
          {note.universityYear
            ? universityYearLabels[note.universityYear] || note.universityYear
            : legacyGradeLabels[note.grade || ''] || note.grade || '—'}
        </span>
        {note.university ? (
          <span
            style={{
              background: 'rgba(99, 102, 241, 0.1)',
              color: '#a5b4fc',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: 600,
              border: '1px solid rgba(99, 102, 241, 0.2)',
            }}
          >
            {note.university}
            {note.department ? ` · ${note.department}` : ''}
          </span>
        ) : null}
        {note.tags.slice(0, 2).map((tag, i) => (
          <span
            key={i}
            style={{
              background: 'rgba(51, 65, 85, 0.4)',
              color: '#94a3b8',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: 500,
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '16px',
          borderTop: '1px solid rgba(51, 65, 85, 0.12)',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '12px',
            color: '#475569',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <User size={11} />
            <span>{getNoteAuthorLabel(note.author)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={11} />
            <span>{timeAgo(note.createdAt)}</span>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={handleDownloadClick}
          className="btn-shimmer"
          whileHover={{ scale: 1.05, boxShadow: '0 6px 25px var(--accent-primary-glow)' }}
          whileTap={{ scale: 0.95 }}
          disabled={isDownloading}
          style={{
            background: isDownloading ? 'var(--accent-primary-glow)' : 'var(--gradient-primary)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '12px',
            border: 'none',
            fontWeight: 600,
            cursor: isDownloading ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            boxShadow: '0 4px 15px var(--shadow-glow)',
            flexShrink: 0,
          }}
        >
          {isDownloading ? (
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
              <Loader size={14} />
            </motion.div>
          ) : (
            <Download size={14} />
          )}
          <span>{isDownloading ? 'İndiriliyor...' : 'İndir'}</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default NoteCard;
